import type { Field } from './fields.js';
import {
  MESSAGE_ORDER,
  Messages,
  type AnyMessage,
  type MessageData,
  type MessageName,
} from './schema.js';

const textEncoder = new TextEncoder();
const textDecoder = new TextDecoder();

class Writer {
  private buf = new ArrayBuffer(256);
  private view = new DataView(this.buf);
  private bytes = new Uint8Array(this.buf);
  private off = 0;

  private ensure(n: number): void {
    if (this.off + n <= this.buf.byteLength) return;
    let size = this.buf.byteLength;
    while (size < this.off + n) size *= 2;
    const next = new ArrayBuffer(size);
    new Uint8Array(next).set(this.bytes);
    this.buf = next;
    this.view = new DataView(next);
    this.bytes = new Uint8Array(next);
  }

  u8(v: number): void {
    this.ensure(1);
    this.view.setUint8(this.off, v);
    this.off += 1;
  }
  u16(v: number): void {
    this.ensure(2);
    this.view.setUint16(this.off, v, true);
    this.off += 2;
  }
  u32(v: number): void {
    this.ensure(4);
    this.view.setUint32(this.off, v, true);
    this.off += 4;
  }
  i16(v: number): void {
    this.ensure(2);
    this.view.setInt16(this.off, v, true);
    this.off += 2;
  }
  i32(v: number): void {
    this.ensure(4);
    this.view.setInt32(this.off, v, true);
    this.off += 4;
  }
  f32(v: number): void {
    this.ensure(4);
    this.view.setFloat32(this.off, v, true);
    this.off += 4;
  }
  str(v: string): void {
    const encoded = textEncoder.encode(v);
    this.u16(encoded.byteLength);
    this.ensure(encoded.byteLength);
    this.bytes.set(encoded, this.off);
    this.off += encoded.byteLength;
  }
  result(): Uint8Array {
    return this.bytes.slice(0, this.off);
  }
}

class Reader {
  private off = 0;
  private readonly view: DataView;
  constructor(private readonly bytes: Uint8Array) {
    this.view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  }
  u8(): number {
    const v = this.view.getUint8(this.off);
    this.off += 1;
    return v;
  }
  u16(): number {
    const v = this.view.getUint16(this.off, true);
    this.off += 2;
    return v;
  }
  u32(): number {
    const v = this.view.getUint32(this.off, true);
    this.off += 4;
    return v;
  }
  i16(): number {
    const v = this.view.getInt16(this.off, true);
    this.off += 2;
    return v;
  }
  i32(): number {
    const v = this.view.getInt32(this.off, true);
    this.off += 4;
    return v;
  }
  f32(): number {
    const v = this.view.getFloat32(this.off, true);
    this.off += 4;
    return v;
  }
  str(): string {
    const len = this.u16();
    const slice = this.bytes.subarray(this.off, this.off + len);
    this.off += len;
    return textDecoder.decode(slice);
  }
  get consumed(): number {
    return this.off;
  }
}

function writeField(w: Writer, field: Field, value: unknown): void {
  switch (field.k) {
    case 'u8':
      return w.u8(value as number);
    case 'u16':
      return w.u16(value as number);
    case 'u32':
      return w.u32(value as number);
    case 'i16':
      return w.i16(value as number);
    case 'i32':
      return w.i32(value as number);
    case 'f32':
      return w.f32(value as number);
    case 'bool':
      return w.u8(value === true ? 1 : 0);
    case 'str':
      return w.str(value as string);
    case 'arr': {
      const items = value as unknown[];
      w.u16(items.length);
      for (const item of items) writeField(w, field.of, item);
      return;
    }
    case 'obj': {
      const record = value as Record<string, unknown>;
      // Ordem de declaração de chave string é garantida pela spec da linguagem;
      // wire.test.ts trava os bytes para pegar qualquer reordenação acidental.
      for (const name of Object.keys(field.fields)) {
        const sub = field.fields[name];
        if (sub === undefined) throw new Error(`campo ausente na spec: ${name}`);
        writeField(w, sub, record[name]);
      }
      return;
    }
  }
}

function readField(r: Reader, field: Field): unknown {
  switch (field.k) {
    case 'u8':
      return r.u8();
    case 'u16':
      return r.u16();
    case 'u32':
      return r.u32();
    case 'i16':
      return r.i16();
    case 'i32':
      return r.i32();
    case 'f32':
      return r.f32();
    case 'bool':
      return r.u8() === 1;
    case 'str':
      return r.str();
    case 'arr': {
      const len = r.u16();
      const items: unknown[] = new Array(len);
      for (let i = 0; i < len; i++) items[i] = readField(r, field.of);
      return items;
    }
    case 'obj': {
      const out: Record<string, unknown> = {};
      for (const name of Object.keys(field.fields)) {
        const sub = field.fields[name];
        if (sub === undefined) throw new Error(`campo ausente na spec: ${name}`);
        out[name] = readField(r, sub);
      }
      return out;
    }
  }
}

export function encode<N extends MessageName>(type: N, data: MessageData<N>): Uint8Array {
  const id = MESSAGE_ORDER.indexOf(type);
  if (id < 0) throw new Error(`mensagem fora de MESSAGE_ORDER: ${type}`);
  const w = new Writer();
  w.u8(id);
  writeField(w, Messages[type], data);
  return w.result();
}

export class DecodeError extends Error {}

export function decode(bytes: Uint8Array): AnyMessage {
  if (bytes.byteLength < 1) throw new DecodeError('pacote vazio');
  const r = new Reader(bytes);
  const id = r.u8();
  const type = MESSAGE_ORDER[id];
  if (type === undefined) throw new DecodeError(`id de mensagem desconhecido: ${id}`);
  let data: unknown;
  try {
    data = readField(r, Messages[type]);
  } catch (cause) {
    throw new DecodeError(`falha ao decodificar ${type}: ${String(cause)}`);
  }
  if (r.consumed !== bytes.byteLength) {
    throw new DecodeError(
      `${type}: ${String(r.consumed)} bytes lidos de ${String(bytes.byteLength)}`,
    );
  }
  return { type, data } as AnyMessage;
}
